import { ProductTypeEnum } from '@koob/enums';

export type ExperienceCompatibility = 'incompatible' | 'bestMatch' | null;

interface Related {
  id?: string | number;
}

interface CompatibilityExperience {
  id?: string | number;
  durationDays?: number;
  incompatibilities?: Related[];
  bestMatches?: Related[];
}

const hasRelation = (
  items: Related[] | undefined,
  targetId: string | number | undefined,
) => !!items?.some((item) => item?.id === targetId);

type Neighbour = CompatibilityExperience | null;

function isIncompatible(
  experience: CompatibilityExperience,
  prevDayExp: Neighbour,
  nextDayExp: Neighbour,
  coveredExperiences: CompatibilityExperience[],
) {
  return (
    // Previous neighbor: the neighbor must reference the candidate experience.
    (!!prevDayExp &&
      hasRelation(prevDayExp.incompatibilities, experience.id)) ||
    // Next neighbor: the candidate experience must reference the next neighbor.
    (!!nextDayExp &&
      hasRelation(experience.incompatibilities, nextDayExp.id)) ||
    // Covered experiences are being overwritten: check both ways.
    coveredExperiences.some(
      (coveredExp) =>
        hasRelation(experience.incompatibilities, coveredExp.id) ||
        hasRelation(coveredExp.incompatibilities, experience.id),
    )
  );
}

function isBestMatch(
  experience: CompatibilityExperience,
  prevDayExp: Neighbour,
  nextDayExp: Neighbour,
) {
  return (
    (!!prevDayExp && hasRelation(prevDayExp.bestMatches, experience.id)) ||
    (!!nextDayExp && hasRelation(experience.bestMatches, nextDayExp.id))
  );
}

/**
 * How a candidate experience fits the selected day's neighbours:
 * 'incompatible', 'bestMatch' or null.
 */
export function getExperienceCompatibility({
  experience,
  days,
  selectedDay,
  selectedType,
  experiencesMap,
}: {
  experience: CompatibilityExperience | null | undefined;
  days: { experienceId?: string | number | null }[] | null | undefined;
  selectedDay: number | null;
  selectedType: string | null | undefined;
  experiencesMap: Record<string, CompatibilityExperience> | null | undefined;
}): ExperienceCompatibility {
  if (
    selectedDay === null ||
    !days ||
    days.length === 0 ||
    selectedType !== ProductTypeEnum.EXPERIENCE ||
    !experience?.id
  ) {
    return null;
  }

  const experienceOf = (dayIndex: number) => {
    const id = days[dayIndex]?.experienceId;
    return id ? (experiencesMap?.[id] ?? null) : null;
  };
  const durationDays = experience.durationDays ?? 1;
  const prevDayExp = experienceOf(selectedDay - 1);
  const nextDayExp = experienceOf(selectedDay + durationDays);
  const coveredExperiences: CompatibilityExperience[] = [];

  for (let i = 1; i < durationDays; i++) {
    const coveredExp = experienceOf(selectedDay + i);
    if (coveredExp?.id) {
      coveredExperiences.push(coveredExp);
    }
  }

  if (isIncompatible(experience, prevDayExp, nextDayExp, coveredExperiences)) {
    return 'incompatible';
  }
  if (isBestMatch(experience, prevDayExp, nextDayExp)) {
    return 'bestMatch';
  }

  return null;
}
